// What to order (purchasing phase 2, docs/plans/reorder.md): how much to suggest for a low
// product, the products grouped by their usual supplier, and the drafts made from them.
import { buildPurchaseOrder, type BuildContext, type SyncOp } from '@ims/core'
import type { ToOrderRow } from '@ims/data'

/**
 * The product's reorder quantity; without one, enough to reach twice its alert level once
 * what is on order arrives. Whole units unless the unit allows decimals.
 */
export function suggestQty(row: ToOrderRow, allowsDecimal = false): number {
  const { min_stock_milli: min, reorder_qty_milli: reorder } = row.product
  if (reorder > 0) return reorder
  const need = Math.max(0, 2 * min - row.stockMilli - row.onOrderMilli)
  const qty = allowsDecimal ? need : Math.ceil(need / 1000) * 1000
  return Math.max(qty, allowsDecimal ? 1 : 1000)
}

export interface ReorderGroup {
  /** Null: products whose usual supplier is not set (or no longer exists). */
  supplierId: string | null
  name: string
  rows: ToOrderRow[]
}

/**
 * By usual supplier, suppliers by name, the products with none last. `moved` puts a product
 * under another supplier for this order only (product id → supplier id); the product's usual
 * supplier is not changed.
 */
export function groupBySupplier(
  rows: readonly ToOrderRow[],
  suppliers: readonly { id: string; name: string }[],
  moved: Readonly<Record<string, string>> = {},
): ReorderGroup[] {
  const names = new Map(suppliers.map((s) => [s.id, s.name]))
  const groups = new Map<string | null, ReorderGroup>()
  for (const row of rows) {
    const wanted = moved[row.product.id] ?? row.product.supplier_id
    const id = wanted && names.has(wanted) ? wanted : null
    let g = groups.get(id)
    if (!g) {
      g = { supplierId: id, name: id ? names.get(id)! : 'No usual supplier', rows: [] }
      groups.set(id, g)
    }
    g.rows.push(row)
  }
  return [...groups.values()].sort((a, b) =>
    a.supplierId === null ? 1 : b.supplierId === null ? -1 : a.name.localeCompare(b.name),
  )
}

export interface DraftPlan {
  supplierId: string
  lines: { productId: string; qtyMilli: number; unitCostPaisa: number; requestLineId?: string }[]
}

/** One draft purchase order per supplier, all in one transaction. Lines of 0 are left out. */
export function buildReorderDrafts(
  ctx: BuildContext,
  plans: readonly DraftPlan[],
  where: { locationId: string | null; orderDate: string },
): { orderIds: string[]; ops: SyncOp[] } {
  const orderIds: string[] = []
  const ops: SyncOp[] = []
  for (const plan of plans) {
    const lines = plan.lines.filter((l) => l.qtyMilli > 0)
    if (lines.length === 0) continue
    const built = buildPurchaseOrder(ctx, {
      status: 'draft',
      supplierId: plan.supplierId,
      locationId: where.locationId,
      orderDate: where.orderDate,
      lines,
    })
    orderIds.push(built.order.id)
    ops.push(...built.ops)
  }
  return { orderIds, ops }
}
